import React from 'react';
import { useNavigate } from 'react-router-dom';
import { shopConfig as cfg } from '../shopConfig';
import { hourRows, schemaOpeningHours } from '../hours';

// Our Story - Red Robin Boutique.
// Hours come from shop_config (cfg.contact.hours) via ../hours - the same record
// that drives the header's open/closed indicator. No second copy to drift.

const EMAIL = 'redrobinboutique@yahoo.com';

const Section: React.FC<{ children: React.ReactNode; tint?: boolean }> = ({ children, tint }) => (
  <section style={{ background: tint ? 'rgba(0,0,0,.025)' : 'transparent', padding: '56px 20px' }}>
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>{children}</div>
  </section>
);

const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 style={{ fontSize: 26, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '0 0 18px' }}>
    {children}
  </h2>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontSize: 16, lineHeight: 1.75, color: 'rgba(0,0,0,.72)', margin: '0 0 16px' }}>{children}</p>
);

export const OurStoryPage: React.FC = () => {
  const navigate = useNavigate();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ClothingStore',
    name: 'Red Robin Boutique',
    description:
      "Women's clothing boutique on Main Street, Ballinagh, Co. Cavan. Dresses, jackets, coats, " +
      'shoes, bags and jewellery in sizes 8 to 24.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Main Street',
      addressLocality: 'Ballinagh',
      addressRegion: 'Co. Cavan',
      postalCode: 'H12T9P9',
      addressCountry: 'IE',
    },
    telephone: cfg.contact.phone || '+353 87 316 0963',
    email: EMAIL,
    openingHours: schemaOpeningHours(),
    priceRange: '€€',
  };

  return (
    <div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <div style={{ position: 'relative', height: 'clamp(260px, 42vw, 440px)', overflow: 'hidden' }}>
        <img
          src="/red-robin-shop-front.jpg"
          alt="Red Robin Boutique shopfront on Main Street, Ballinagh"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,.15) 0%, rgba(0,0,0,.62) 100%)' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 20px 34px', textAlign: 'center' }}>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 900, fontStyle: 'italic', color: 'white', margin: 0, textShadow: '0 2px 12px rgba(0,0,0,.4)' }}>
            Our Story
          </h1>
          <p style={{ fontSize: 'clamp(13px, 2vw, 16px)', color: 'rgba(255,255,255,.9)', margin: '10px 0 0', letterSpacing: '.04em' }}>
            Style meets sophistication, on Main Street in Ballinagh
          </p>
        </div>
      </div>

      {/* The beginning */}
      <Section>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 36, alignItems: 'center' }}>
          <div>
            <H2>The beginning</H2>
            <P>
              Red Robin Boutique opened its doors on Main Street, Ballinagh in the summer of 2023.
              Philomena Connolly had spotted a gap: fashionable, good quality clothing that local
              women actually wanted to wear, at prices that made sense. It was also, as she puts it,
              "something that I always wanted to do."
            </P>
            <P>
              Two years on, the shop has become a fixture of the town - and word has travelled a good
              deal further than Cavan. Customers now come from as far as Donegal, Offaly and Cork.
            </P>
          </div>
          <img
            src="/red-robin-desk.jpg"
            alt="Inside Red Robin Boutique, Ballinagh"
            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 2 }}
          />
        </div>
      </Section>

      {/* What you'll find */}
      <Section tint>
        <H2>What you'll find</H2>
        <P>
          A bright, modern boutique with a bit of everything: dresses, jackets and coats, shirts,
          trousers and jeans, jumpers and tee-shirts, leisure wear, along with shoes, bags and
          jewellery.
        </P>
        <P>
          We carry sizes 8 to 24. The collection is chosen with women forty plus in mind, with plenty
          of picks for younger looks too - and most of our dresses sit in the €50 to €100 range.
        </P>
        <P>Dresses are a big part of what we do, from everyday to occasion.</P>
        <button
          onClick={() => navigate('/shop')}
          style={{ marginTop: 8, background: cfg.brand.accent, color: cfg.brand.secondary, border: 'none', padding: '12px 26px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}
        >
          Browse the collection
        </button>
      </Section>

      {/* More than a shop */}
      <Section>
        <H2>More than a shop</H2>
        <P>
          Red Robin hosts fashion and beauty talks and workshops through the year, and we're proud of
          what the shop has been able to do for causes close to home - a fashion show in aid of Cuan
          Cancer at the Farnham Estate, and a fundraiser for Brain Tumour Ireland in honour of our
          friend and colleague Sinead Reilly.
        </P>
      </Section>

      {/* Recognition */}
      <Section tint>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 36, alignItems: 'center' }}>
          <img
            src="/red-robin-tom-phil.jpg"
            alt="Tom and Philomena Connolly of Red Robin Boutique at the Anglo-Celt Business Awards pitch night"
            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 2 }}
          />
          <div>
            <H2>Recognition</H2>
            <P>
              In 2024 Red Robin Boutique was shortlisted as a finalist in the Anglo-Celt Cavan
              Business and Tourism Awards, in the Best in Fashion and Lifestyle category.
            </P>
            <blockquote style={{ margin: 0, paddingLeft: 18, borderLeft: `3px solid ${cfg.brand.accent}`, fontStyle: 'italic', fontSize: 16, lineHeight: 1.7, color: 'rgba(0,0,0,.72)' }}>
              "I've put a lot of hard work into getting to where I am with the business, and I feel
              that getting shortlisted is recognition of that."
              <footer style={{ marginTop: 10, fontStyle: 'normal', fontSize: 13, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: cfg.brand.secondary }}>
                Philomena Connolly
              </footer>
            </blockquote>
          </div>
        </div>
      </Section>

      {/* Visit us */}
      <Section>
        <H2>Visit us</H2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 36 }}>
          <div>
            <P>
              <strong>Red Robin Boutique</strong>
              <br />
              Main Street, Ballinagh, Co. Cavan, H12T9P9
            </P>
            <P>Parking is available behind the building.</P>
            <P>
              Phone: <a href="tel:+353873160963" style={{ color: cfg.brand.secondary }}>+353 87 316 0963</a>
              <br />
              Email: <a href={`mailto:${EMAIL}`} style={{ color: cfg.brand.secondary }}>{EMAIL}</a>
            </P>
            <P>We deliver across Ireland, the UK and worldwide.</P>
          </div>
          <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
              <tbody>
                {hourRows().map((h) => (
                  <tr key={h.day} style={{ borderBottom: '1px solid rgba(0,0,0,.08)' }}>
                    <td style={{ padding: '10px 0', fontWeight: 700, color: cfg.brand.secondary }}>{h.day}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: h.closed ? 'rgba(0,0,0,.4)' : 'rgba(0,0,0,.72)' }}>
                      {h.text}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </div>
  );
};